import { FC } from 'react'

import { useParams } from 'react-router-dom'

import {
  apiKeyCreateRules,
  apiKeyFieldOverrides,
} from 'pages/companies/APIKeys/apiKeyFormRules'

import {
  ResourceCreateView,
  ResourceDetailView,
  ResourceListView,
  useResourceListPaging,
} from 'core/resources'
import {
  useInternalCompanyApiKeyCreate,
  useInternalCompanyApiKeyDelete,
  useInternalCompanyApiKeyGet,
  useInternalCompanyApiKeyUpdate,
  useInternalCompanyApiKeysGetAll,
} from 'core/types/generated/api-keys/hooks'
import { resourceCatalog } from 'core/types/generated/resources'
import {
  keysRequestSchema,
  keysResponseSchema,
  keysUpdateRequestSchema,
} from 'core/utils/generated/formSchemas'

import { asRequestBody, createdId, listRows, record } from './helpers'

const resource = resourceCatalog.keys

type ListQuery = Parameters<typeof useInternalCompanyApiKeysGetAll>[0]['query']

export const KeysList: FC = () => {
  const { companyId } = useParams<{ companyId: string }>()
  const paging = useResourceListPaging<ListQuery>('cursor')
  const result = useInternalCompanyApiKeysGetAll({
    companyId,
    query: paging.query,
  })

  return (
    <ResourceListView
      {...listRows(result)}
      paging={paging}
      resource={resource}
      schema={keysResponseSchema}
    />
  )
}

export const KeyCreate: FC = () => {
  const { companyId } = useParams<{ companyId: string }>()
  const { mutateAsync } = useInternalCompanyApiKeyCreate({ companyId })

  return (
    <ResourceCreateView
      fieldOverrides={apiKeyFieldOverrides}
      onCreate={async (body) =>
        createdId(await mutateAsync(asRequestBody(body)))
      }
      resource={resource}
      schema={keysRequestSchema}
      validationRules={apiKeyCreateRules}
    />
  )
}

export const KeyDetail: FC = () => {
  const { companyId, keyId } = useParams<{ companyId: string; keyId: string }>()
  const result = useInternalCompanyApiKeyGet({ companyId, keyId })
  const update = useInternalCompanyApiKeyUpdate({ companyId, keyId })
  const remove = useInternalCompanyApiKeyDelete({ companyId, keyId })

  return (
    <ResourceDetailView
      {...record(result)}
      fieldOverrides={apiKeyFieldOverrides}
      onDelete={async () => {
        await remove.mutateAsync()
      }}
      onSave={async (body) => {
        await update.mutateAsync(asRequestBody(body))
      }}
      resource={resource}
      schema={keysResponseSchema}
      updateSchema={keysUpdateRequestSchema}
    />
  )
}
